import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { Moment } from 'moment';
import moment from 'moment';
import type { Appointment } from 'services/types/appointment';
import { SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import AppointmentCard from './AppointmentCard';
import { END_HOUR, HOUR_HEIGHT, START_HOUR } from './serviceCalendar.constants';
import { appointmentsOnDay, formatHourLabel } from './serviceCalendar.utils';

const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

interface PositionedEvent {
  appointment: Appointment;
  top: number;
  height: number;
  lane: number;
  lanes: number;
}

/** Assign overlapping appointments to side-by-side lanes (greedy interval packing). */
const layoutDay = (dayAppointments: Appointment[]): PositionedEvent[] => {
  const sorted = [...dayAppointments].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
  const laneEnds: number[] = [];
  const placed = sorted.map((appointment) => {
    const start = moment(appointment.date);
    const startMinutes = start.hours() * 60 + start.minutes();
    const endMinutes = startMinutes + appointment.durationMinutes;
    let lane = laneEnds.findIndex((end) => end <= startMinutes);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(endMinutes);
    } else {
      laneEnds[lane] = endMinutes;
    }
    const top = ((startMinutes - START_HOUR * 60) / 60) * HOUR_HEIGHT;
    const height = Math.max((appointment.durationMinutes / 60) * HOUR_HEIGHT - 4, 26);
    return { appointment, top, height, lane };
  });
  const lanes = laneEnds.length || 1;
  return placed.map((p) => ({ ...p, lanes }));
};

interface Props {
  days: Moment[];
  appointments: Appointment[];
  onSelect: (appointment: Appointment) => void;
}

const TimelineGrid = ({ days, appointments, onSelect }: Props) => {
  const gridHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT;

  return (
    <Box sx={{ display: 'flex', overflow: 'auto', flex: 1, minHeight: 0 }}>
      {/* Hour gutter */}
      <Box sx={{ width: 56, flexShrink: 0, pt: '6px' }}>
        {HOURS.map((hour) => (
          <Box key={hour} sx={{ height: HOUR_HEIGHT, position: 'relative' }}>
            <Typography sx={{ position: 'absolute', top: -8, right: 8, fontSize: 11, color: 'rgba(21, 24, 58, 0.4)' }}>
              {formatHourLabel(hour)}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Day columns */}
      <Box sx={{ display: 'flex', flex: 1, minWidth: days.length > 1 ? days.length * 150 : 0 }}>
        {days.map((day) => {
          const dayAppointments = appointmentsOnDay(appointments, day);
          const positioned = dayAppointments.length ? layoutDay(dayAppointments) : [];
          return (
            <Box
              key={day.toISOString()}
              sx={{
                flex: 1,
                position: 'relative',
                height: gridHeight,
                backgroundColor: SURFACE_CONTAINER_LOWEST,
                // Tonal separation between columns instead of 1px borders.
                boxShadow: 'inset 1px 0 0 rgba(21, 24, 58, 0.04)',
              }}
            >
              {/* Hour guide lines (ghost) */}
              {HOURS.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: (hour - START_HOUR) * HOUR_HEIGHT,
                    height: '1px',
                    backgroundColor: 'rgba(21, 24, 58, 0.05)',
                  }}
                />
              ))}

              {positioned.map(({ appointment, top, height, lane, lanes }) => (
                <Box
                  key={appointment.id}
                  sx={{
                    position: 'absolute',
                    top,
                    height,
                    left: `calc(${(lane / lanes) * 100}% + 4px)`,
                    width: `calc(${100 / lanes}% - 8px)`,
                  }}
                >
                  <AppointmentCard appointment={appointment} variant="timeline" onClick={onSelect} />
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default TimelineGrid;
